import { MetadataOptionsQuestion, NumberQuestionBase } from '../types/questions';
import { normalizeMeterConfigurations } from './meterConfigurations';

export interface ResolvedMeterLink {
  meterId: string;
  assetQuestionId: string;
  /**
   * The asset question itself, not just its id. Callers need it to read the selected asset, and
   * returning only the id made them look it up again — with a plain `find` that does not flatten
   * table nesting, so the second lookup disagreed with this one on exactly the nested case. One
   * definition means returning what the resolution found, not making callers re-derive it.
   */
  assetQuestion: MetadataOptionsQuestion;
}

/** Minimal shape this module needs; table questions nest their own children under `questions`. */
interface LinkCandidate {
  id: string;
  type: string;
  questions?: LinkCandidate[];
}

/** A question paired with the id of the repeatable group holding it, or undefined at top level. */
interface FlatCandidate {
  question: LinkCandidate;
  tableId?: string;
}

/**
 * Flattens the form while remembering where each question came from. The nesting level is part
 * of the predicate (check 6), so a flat list that forgets it cannot answer the question.
 */
function flattenQuestions(questions: LinkCandidate[], tableId?: string): FlatCandidate[] {
  return questions.flatMap((question) =>
    question.type === 'table' && question.questions?.length
      ? [{ question, tableId }, ...flattenQuestions(question.questions, question.id)]
      : [{ question, tableId }],
  );
}

const sameId = (a: unknown, b: unknown) => String(a) === String(b);

/**
 * Resolves the meter a number question is actually linked to, or null when the link is
 * incomplete.
 *
 * THE LINK PREDICATE. All six checks must hold. This list is the cross-surface contract:
 * the web builder, the web answering runtime and the mobile app must each apply the same
 * six, or the same form validates on one surface and not on another.
 *
 *   1. `question.associatedMetadata` carries both `meterId` and `questionId`.
 *   2. A question with id `associatedMetadata.questionId` exists in the form.
 *   3. That question is an asset list: `type === 'select_metadata'` AND `'assets'` in either
 *      `metadata` or `metadata_code`.
 *
 *      Both fields, and not as a precaution: neither one alone is correct. The builder authors
 *      the question client-side as `metadata: 'assets'` (formBuilder.tsx), leaving
 *      `metadata_code` empty. When the backend serves the form it moves the marker from one
 *      field to the other (task_form.rb:88-104): it reads `metadata`, loads the account's
 *      assets, and then overwrites `element["metadata"]` with those rows while writing the
 *      marker to `element["metadata_code"]`. So in the served shape — what the answering
 *      runtime and the app see — `metadata` is an array of asset rows, not the string.
 *      Checking only `metadata_code` is always false in the builder; checking only `metadata`
 *      is always false once served; checking only `meterConfigurations` claims "asset list"
 *      when all it knows is "someone configured meters".
 *   4. It has at least one meter configuration, read through `normalizeMeterConfigurations`.
 *      Reading the raw `meterConfigurations` field rejects every question saved before that
 *      array existed, which carries a bare `meterId` instead, and the author has no way to see
 *      why: the comparator stays unavailable while the copy tells them to do what they already
 *      did.
 *   5. One of those configurations has `isLocation` falsy, lists this question's `id` in
 *      `associatedQuestions`, and its `meterId` equals `associatedMetadata.meterId`. Every id
 *      here is compared as a string, in this check and in the search of check 2: ids arrive as
 *      numbers from some payloads and as strings from others, and a predicate that coerces in
 *      one place and not in another is exactly what a mirroring surface copies wrong.
 *   6. Neither end of the link sits inside a repeatable group. Answering a question in a group
 *      never updates a meter (product decision), so the last reading never moves and there is
 *      nothing to validate against. `MeterConfigItem` already enforces same-level linking when
 *      the author builds it, so a cross-level link is not something the builder can produce.
 *
 *      This check lives here rather than in the builder's render for a concrete reason:
 *      `handleCombine` (formBuilder.tsx) can move a top-level question that already carries a
 *      saved `withinMargin` configuration into a table. The builder then hides the option, but
 *      the configuration stays saved, and any surface that reads the predicate instead of the
 *      prop would still evaluate it.
 *
 * They are enumerated one per line rather than grouped on purpose: a grouped list invites a
 * surface mirroring this predicate to collapse them, and the one that gets dropped is check 3.
 *
 * Check 5 is the one that is easy to miss and the one that matters. A number question carries
 * `associatedMetadata.meterId`, but that alone does not mean the link is live: the asset
 * question on the other side must still list this question. The two sides drift apart when a
 * question is duplicated — `onDuplicateHandler` (formBuilder.tsx) copies `associatedMetadata`
 * verbatim onto the new question, along with any `withinMargin` configuration, while the meter
 * configuration keeps pointing at the original id only. Skipping check 5 means happily
 * validating a duplicated question against a meter that no longer claims it.
 *
 * Note this predicate is deliberately static: it says whether the wiring is intact, not
 * whether an asset is currently selected. Resolving the selected asset depends on answers and
 * has a different lifecycle, so it belongs to the caller.
 */
export function resolveMeterLink(
  question: NumberQuestionBase,
  questions: LinkCandidate[],
): ResolvedMeterLink | null {
  if (question.type !== 'number') return null;

  const { meterId, questionId } = question.associatedMetadata ?? {};
  // Empty strings are absence, not a value: the doc says the metadata must carry both, and a
  // surface mirroring `!= null` would accept a half-written link.
  if (!meterId || !questionId) return null;

  const flattened = flattenQuestions(questions);
  const assetEntry = flattened.find((entry) => sameId(entry.question.id, questionId));
  const assetQuestion = assetEntry?.question as MetadataOptionsQuestion | undefined;

  const assetMarkers = [assetQuestion?.metadata, assetQuestion?.metadata_code];
  if (assetQuestion?.type !== 'select_metadata' || !assetMarkers.includes('assets')) {
    return null;
  }

  // Check 6, before the configuration search so the reason is one condition and not a side
  // effect of a failed find. The number question's own entry may be absent from `questions` when
  // a caller passes only part of the form, and an absent entry says nothing about nesting, so
  // only a found entry can disqualify it.
  const numberEntry = flattened.find((entry) => sameId(entry.question.id, question.id));
  if (numberEntry?.tableId != null || assetEntry?.tableId != null) return null;

  const configurations = normalizeMeterConfigurations(assetQuestion);
  if (!configurations.length) return null;

  // All three conditions belong in the predicate, not two in the search and one after it: an
  // asset can carry several configurations listing the same question, and finding the first and
  // then rejecting it on its meter would miss a later, genuinely matching one.
  const meterConfig = configurations.find(
    (config) =>
      !config.isLocation &&
      config.associatedQuestions?.some((id) => sameId(id, question.id)) &&
      sameId(config.meterId, meterId),
  );
  if (!meterConfig) return null;

  return { meterId: String(meterId), assetQuestionId: assetQuestion.id, assetQuestion };
}
